import { z } from "zod";
import { NumParam, Slug } from "./common.ts";

/** GET /api/depth?lat=&lon= */
export const DepthQuery = z
  .object({
    lat: NumParam.pipe(z.number().min(-90).max(90)),
    lon: NumParam.pipe(z.number().min(-180).max(180)),
  })
  .strict();
export type DepthQuery = z.infer<typeof DepthQuery>;

/**
 * - `ok`: depthM = waterLevelM − bottomElevM, always ≥ 0.
 * - `dry`: inside a bathy lake's grid but the cell is at/above the water level.
 * - `no_data`: inside a bathy lake's bbox but the cell is nodata (e.g. unsurveyed shallows).
 *   Never report nodata as 0 m.
 * - `no_lake`: not inside any lake with bathymetry.
 */
export const DepthResult = z.discriminatedUnion("status", [
  z
    .object({
      status: z.literal("ok"),
      lakeSlug: Slug,
      bottomElevM: z.number(),
      waterLevelM: z.number(),
      depthM: z.number().min(0),
    })
    .strict()
    .refine((d) => Math.abs(d.depthM - (d.waterLevelM - d.bottomElevM)) < 0.01, {
      message: "depthM must equal waterLevelM − bottomElevM",
      path: ["depthM"],
    }),
  z.object({ status: z.literal("dry"), lakeSlug: Slug }).strict(),
  z.object({ status: z.literal("no_data"), lakeSlug: Slug }).strict(),
  z.object({ status: z.literal("no_lake") }).strict(),
]);
export type DepthResult = z.infer<typeof DepthResult>;
